// Route Repository for Dexie Operations
// Handles route data persistence in IndexedDB for offline-first functionality

import type { Route } from '@/domains/route/types/Route';
import { db } from '@/db/dexieClient';
import { uow, UnitOfWork } from '@/db/unitOfWork';
import { syncService, SyncJobTypes } from '@/services/sync/syncService';
import { liveQuery } from 'dexie';
import { upsertEntities, type SyncableEntity, type UpsertConfig } from '@/services/sync/syncUtils';

const routeByIdQuery = (routeId: number) => db.routes.where('id').equals(routeId).first();

const allRoutesQuery = async () => db.routes.toArray();

// --- Type Definitions ---

// Generic partial type for updates
// For Dexie .update, use shallow updates (no nested objects/arrays, just the ones that potentially might change)
export type RouteUpdate = Partial<
  Pick<Route, 'statusCodeId' | 'syncStatus' | 'updatedDate' | 'startedOn'>
>;

// --- Public API: Get Operations ---
export const getRouteFromCacheById = async (routeId: number): Promise<Route | null> => {
  try {
    return (await routeByIdQuery(routeId)) ?? null;
  } catch (error) {
    console.error('Failed to get route from cache:', error);
    return null;
  }
};

export const observeRouteById = (routeId: number) =>
  liveQuery(async () => (await routeByIdQuery(routeId)) ?? null);

export const getAllRoutesFromCache = async (): Promise<Route[]> => {
  try {
    return await allRoutesQuery();
  } catch (error) {
    console.error('Failed to get routes from cache:', error);
    return [];
  }
};

export const observeAllRoutes = () => liveQuery(async () => (await db.routes.toArray()) ?? []);

// --- Public API: Update Operations ---

export const updateRoute = async (id: number, changes: RouteUpdate): Promise<void> => {
  const oldRecord = (await db.routes.get(id)) as Route;
  const newRecord = {
    ...oldRecord,
    ...changes,
  };
  await uow.runRW([db.routes], async () => db.routes.update(id, changes));

  try {
    await syncService.enqueue({
      type: SyncJobTypes.ROUTE_UPDATE,
      newRecord: newRecord,
      oldRecord: oldRecord,
    });
  } catch (error) {
    console.error('Failed to clear routes cache:', error);
    throw error;
  }
};

// --- Public API: Sync Operations ---

// Receives api routes and returns the updated routes from the cache
export const upsertRoutes = async (apiRoutes: Route[]): Promise<Route[]> => {
  try {
    const cachedRoutes = await getAllRoutesFromCache();

    const config: UpsertConfig<Route & SyncableEntity> = {
      getId: (route) => route.id,
      markAsPending: (route) => ({
        ...route,
        syncStatus: 'pending',
        syncedOn: null,
      }),
    };

    const { toInsert, toUpdate, toDelete, toKeepLocal } = upsertEntities(
      apiRoutes as (Route & SyncableEntity)[],
      cachedRoutes as (Route & SyncableEntity)[],
      config
    );

    await uow.runRW([db.routes], async () => {
      // Insert new routes
      if (toInsert.length > 0) {
        const plainRoutes = UnitOfWork.toPlain(toInsert);
        await db.routes.bulkPut(plainRoutes);
      }

      // Update existing routes with API data
      if (toUpdate.length > 0) {
        const plainRoutes = UnitOfWork.toPlain(toUpdate);
        await db.routes.bulkPut(plainRoutes);
      }

      // Update routes that should be kept locally (mark as pending)
      if (toKeepLocal.length > 0) {
        const plainRoutes = UnitOfWork.toPlain(toKeepLocal);
        await db.routes.bulkPut(plainRoutes);
      }

      // Delete routes not in API response
      if (toDelete.length > 0) {
        await db.routes.bulkDelete(toDelete);
      }
    });

    // Get updated routes from cache after upsert
    const updatedRoutes = await getAllRoutesFromCache();

    console.info('[routeRepository] Sync completed:', {
      inserted: toInsert.length,
      updated: toUpdate.length,
      deleted: toDelete.length,
      keptLocal: toKeepLocal.length,
    });

    if (toKeepLocal.length > 0) console.warn('[routeRepository] Routes kept local:', toKeepLocal);

    return updatedRoutes;
  } catch (error) {
    console.error('Failed to upsert routes:', error);
    throw error;
  }
};

// --- Public API: Delete Operations ---

export const clearRoutesCache = async (): Promise<void> => {
  try {
    await uow.runRW(
      [db.brokers, db.routes, db.routeStops, db.shipments, db.addresses],
      async () => {
        await db.brokers.clear();
        await db.routes.clear();
        await db.routeStops.clear();
        await db.shipments.clear();
        await db.addresses.clear();
      }
    );
  } catch (error) {
    console.error('Failed to clear routes cache:', error);
    throw error;
  }
};
